import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

// Refreshes authentication token periodically to keep the session active
export default function AuthRefreshMonitor() {
  const { currentUser, refreshToken } = useAuth();
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());
  
  // Refresh token immediately on mount if user exists
  useEffect(() => {
    if (currentUser) {
      let isMounted = true;
      
      (async () => {
        try {
          console.log('Initial auth token refresh...');
          const token = await refreshToken();
          
          // Only update state if component is still mounted
          if (isMounted) {
            if (token) {
              console.log('Initial token refresh successful');
              setLastRefresh(Date.now());
            } else {
              console.warn('Initial token refresh returned null');
              // Try again after 3 seconds
              setTimeout(async () => {
                if (isMounted && currentUser) {
                  console.log('Retrying initial token refresh...');
                  const retryToken = await refreshToken();
                  if (retryToken && isMounted) {
                    setLastRefresh(Date.now());
                  }
                }
              }, 3000);
            }
          }
        } catch (err) {
          console.error('Initial token refresh failed:', err);
        }
      })();
      
      return () => {
        isMounted = false;
      };
    }
  }, [currentUser]);
  
  // Refresh token every 10 minutes if user is still active
  useEffect(() => {
    if (!currentUser) return;
    
    let lastActivity = lastRefresh;
    
    const intervalId = setInterval(async () => {
      try {
        // Only refresh if user has been active in last 30 minutes
        const userIsActive = Date.now() - lastActivity < 30 * 60 * 1000;
        
        if (userIsActive) {
          console.log('Refreshing auth token...');
          await refreshToken();
          lastActivity = Date.now();
          setLastRefresh(lastActivity);
        }
      } catch (err) {
        console.error('Failed to refresh authentication token:', err);
      }
    }, 10 * 60 * 1000); // Every 10 minutes
    
    // Update last activity time on user interactions (throttled to prevent excessive state updates)
    let throttleTimeout: NodeJS.Timeout | null = null;
    const updateActivity = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          lastActivity = Date.now();
          setLastRefresh(lastActivity);
          throttleTimeout = null;
        }, 5000); // Throttle to once every 5 seconds
      }
    };
    
    // Listen for user activity
    window.addEventListener('click', updateActivity, { passive: true });
    window.addEventListener('keypress', updateActivity, { passive: true });
    window.addEventListener('scroll', updateActivity, { passive: true });
    window.addEventListener('mousemove', updateActivity, { passive: true });
    
    return () => {
      clearInterval(intervalId);
      if (throttleTimeout) clearTimeout(throttleTimeout);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('mousemove', updateActivity);
    };
  }, [currentUser]);
  
  // This is an invisible component that just handles the token refreshing
  return null;
}
